import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { apiClient } from 'src/api/apiClient';
import {
  GET_WATERFALL_DEFAULT_SOURCES,
  SET_ENRICHMENT_SOURCES,
  SET_ACCOUNTS_ENRICHMENT_SOURCES,
} from 'src/constants/httpEndPoints';

interface WaterfallSource {
  enrichment_source_id: string;
  source_name: string;
  source_code: string;
  source_logo: string;
  field: string;
  entity_type: string;
  price: number;
  sort_order: number;
}

export interface FormattedWaterfallSource {
  name: string;
  credits: number;
  id: string;
  comingSoon?: boolean;
}

interface WaterfallSources {
  emailSources: FormattedWaterfallSource[];
  phoneSources: FormattedWaterfallSource[];
  revenueSources: FormattedWaterfallSource[];
  fundingSources: FormattedWaterfallSource[];
  allSources: Record<string, { sources: FormattedWaterfallSource[] }>;
  accountsPrioritySources: Record<string, string[]>;
  contactsPrioritySources: Record<string, string[]>;
  sourceNamesMap: Record<string, string>;
  sourceIconsMap: Record<string, string>;
  loading: boolean;
  error: string | null;
}

const initialState: WaterfallSources = {
  emailSources: [],
  phoneSources: [],
  revenueSources: [],
  fundingSources: [],
  allSources: {},
  accountsPrioritySources: {},
  contactsPrioritySources: {},
  sourceNamesMap: {},
  sourceIconsMap: {},
  loading: false,
  error: null,
};

interface FetchWaterfallSourcesParams {
  emailID: string;
  customerId: string;
}

export const fetchWaterfallSources = createAsyncThunk(
  'waterfallSources/fetch',
  async ({ emailID, customerId }: FetchWaterfallSourcesParams) => {
    const response = await apiClient.get(`${GET_WATERFALL_DEFAULT_SOURCES}?is_active=true`, {
      headers: {
        'X-Login-Email': emailID,
        'X-Customer-Id': customerId,
      },
    });
    return response.data;
  },
);

interface FetchPrioritySourcesParams {
  emailID: string;
  customerId: string;
  entityType?: 'CONTACT' | 'ACCOUNT';
}

interface PrioritySourceResponseItem {
  field: string;
  prioritySources: string[];
}

export const fetchPrioritySources = createAsyncThunk(
  'waterfallSources/fetchPriority',
  async ({ emailID, customerId, entityType = 'CONTACT' }: FetchPrioritySourcesParams) => {
    const apiUrl =
      entityType === 'ACCOUNT' ? SET_ACCOUNTS_ENRICHMENT_SOURCES : SET_ENRICHMENT_SOURCES;
    const response = await apiClient.get(`${apiUrl}`, {
      headers: {
        'X-Login-Email': emailID,
        'X-Customer-Id': customerId,
      },
    });
    return { data: response.data, entityType };
  },
);

const transformSource = (source: WaterfallSource): FormattedWaterfallSource => ({
  name: source.source_name,
  credits: source.price,
  id: source.source_code,
});

const waterfallSourcesSlice = createSlice({
  name: 'waterfallSources',
  initialState,
  reducers: {
    setWaterfallSources: (state, action) => {
      const payload = action.payload;
      const sourceNamesMap = payload.reduce(
        (acc: Record<string, string>, source: WaterfallSource) => {
          acc[source.source_code] = source.source_name;
          return acc;
        },
        {},
      );
      const sourceIconsMap = payload.reduce(
        (acc: Record<string, string>, source: WaterfallSource) => {
          acc[source.source_code] = source.source_logo;
          return acc;
        },
        {},
      );

      // Single loop to group sources by field type
      const sourcesByField = payload.reduce(
        (acc: Record<string, FormattedWaterfallSource[]>, source: WaterfallSource) => {
          const field = source.field;
          if (!acc[field]) {
            acc[field] = [];
          }
          acc[field].push(transformSource(source));
          return acc;
        },
        {},
      );

      const emailSources = sourcesByField['EMAIL'] || [];
      const phoneSources = sourcesByField['PHONE'] || [];
      const revenueSources = sourcesByField['ANNUAL_REVENUE'] || [];
      const fundingSources = sourcesByField['FUNDING'] || [];

      const defaultAllSourceTypesData = {
        ANNUAL_REVENUE: {
          sources: revenueSources,
        },
        FUNDING: {
          sources: fundingSources,
        },
        PHONE: {
          sources: phoneSources,
        },
        EMAIL: {
          sources: emailSources,
        },
      };

      state.sourceNamesMap = sourceNamesMap;
      state.sourceIconsMap = sourceIconsMap;
      state.emailSources = emailSources;
      state.phoneSources = phoneSources;
      state.revenueSources = revenueSources;
      state.fundingSources = fundingSources;
      state.allSources = defaultAllSourceTypesData;
    },
    setAccountsPrioritySources: (state, action) => {
      state.accountsPrioritySources = action.payload;
    },
    setContactsPrioritySources: (state, action) => {
      state.contactsPrioritySources = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWaterfallSources.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWaterfallSources.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload;
        const sourceNamesMap = payload.reduce(
          (acc: Record<string, string>, source: WaterfallSource) => {
            acc[source.source_code] = source.source_name;
            return acc;
          },
          {},
        );
        const sourceIconsMap = payload.reduce(
          (acc: Record<string, string>, source: WaterfallSource) => {
            acc[source.source_code] = source.source_logo;
            return acc;
          },
          {},
        );

        // Single loop to group sources by field type
        const sourcesByField = payload.reduce(
          (acc: Record<string, FormattedWaterfallSource[]>, source: WaterfallSource) => {
            const field = source.field;
            if (!acc[field]) {
              acc[field] = [];
            }
            acc[field].push(transformSource(source));
            return acc;
          },
          {},
        );

        const emailSources = sourcesByField['EMAIL'] || [];
        const phoneSources = sourcesByField['PHONE'] || [];
        const revenueSources = sourcesByField['ANNUAL_REVENUE'] || [];
        const fundingSources = sourcesByField['FUNDING'] || [];

        const defaultAllSourceTypesData = {
          ANNUAL_REVENUE: {
            sources: revenueSources,
          },
          FUNDING: {
            sources: fundingSources,
          },
          PHONE: {
            sources: phoneSources,
          },
          EMAIL: {
            sources: emailSources,
          },
        };

        state.sourceNamesMap = sourceNamesMap;
        state.sourceIconsMap = sourceIconsMap;
        state.emailSources = emailSources;
        state.phoneSources = phoneSources;
        state.revenueSources = revenueSources;
        state.fundingSources = fundingSources;
        state.allSources = defaultAllSourceTypesData;
      })
      .addCase(fetchWaterfallSources.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch waterfall sources';
      })
      .addCase(fetchPrioritySources.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPrioritySources.fulfilled, (state, action) => {
        state.loading = false;
        const { data, entityType } = action.payload;
        const prioritySourcesMap = data.reduce(
          (acc: Record<string, string[]>, item: PrioritySourceResponseItem) => {
            if (item.field && item.prioritySources) {
              acc[item.field] = item.prioritySources;
            }
            return acc;
          },
          {},
        );
        if (entityType === 'ACCOUNT') {
          state.accountsPrioritySources = prioritySourcesMap;
        } else {
          state.contactsPrioritySources = prioritySourcesMap;
        }
      })
      .addCase(fetchPrioritySources.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch priority sources';
      });
  },
});

export const { setWaterfallSources, setAccountsPrioritySources, setContactsPrioritySources } =
  waterfallSourcesSlice.actions;
export default waterfallSourcesSlice.reducer;
